import PropTypes from 'prop-types';

import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import ShowDocumentContainer from '../../../Messages/containers/ShowDocumentContainer';

import ExpandableImageComponent from '../../../Image/ExpandableImageComponent';
import VisibleToComponent from './VisibleToComponent';

import { avatarPlaceholderSRC, isAdmin } from '../../../../utils';
import { getLinkToProperty, getLinkToUser } from '../../../Messages/utils';
import { getFullName } from '../../../Person/utils';

import { ButtonLink } from '../../../Button';
import WithLink from '../../../Linkify';

const ignoredProps = ['isUserMessage'];

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !ignoredProps.includes(prop),
})(({ theme, isUserMessage }) => ({
  display: 'flex',
  marginTop: '2rem',
  backgroundColor: theme.palette.custom.tab,
  borderRadius: '0 1.5rem 1.5rem 1.5rem',
  padding: '1.5rem',
  paddingBottom: '1.5rem',
  width: 'fit-content',
  marginRight: 'auto',
  minWidth: '25rem',
  ...(isUserMessage && {
    backgroundColor: theme.palette.custom.primary2,
    borderRadius: '1.5rem 0 1.5rem 1.5rem',
    color: theme.palette.common.white,
    marginLeft: 'auto',
    marginRight: 0,
  }),
}));

const ThreadItemComponent = ({
  message,
  isUserMessage,
  onReply,
  buildings,
  isMultiThread,
}) => {
  const { t } = useTransl();

  const {
    sender: person = null,
    text = null,
    document = null,
    createdAt = null,
  } = message ?? {};
  const { personRoleId = null, firstName = '', lastName = '' } = person ?? {};
  const isReplyable = message?.replyable && !isAdmin();

  const userLink = getLinkToUser(person);
  const personName = getFullName(person);
  const propertyLink =
    buildings && !isUserMessage && getLinkToProperty(person, buildings);
  const messageDate = new Date(createdAt)
    .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    .toLocaleLowerCase();

  return (
    <StyledContainer isUserMessage={isUserMessage}>
      <Box
        sx={[{ marginRight: '1.5rem' }, isUserMessage && { display: 'none' }]}
      >
        <ExpandableImageComponent
          src={avatarPlaceholderSRC}
          photo={person?.photo}
          caption={`${firstName} ${lastName}`}
          height="6rem"
          isSquare
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box
          sx={[
            { display: 'flex', justifyContent: 'space-between' },
            isUserMessage && { justifyContent: 'flex-end' },
          ]}
          mb="0.5rem"
        >
          <Box
            display={isUserMessage ? 'none' : 'flex'}
            sx={{ wordBreak: 'break-word' }}
          >
            <Box mr="0.5rem">{userLink || personName}</Box>
            {propertyLink ? <Box>@&nbsp;{propertyLink}&nbsp;</Box> : null}
          </Box>
          <Box>{messageDate}</Box>
        </Box>
        <Box mb="0.4rem">
          {text && (
            <Box sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
              <WithLink>{text}</WithLink>
            </Box>
          )}
          <ShowDocumentContainer document={document} />
        </Box>
        {isReplyable || isMultiThread ? (
          <Box display="flex" justifyContent="space-between">
            <Box>
              {isReplyable ? (
                <ButtonLink onClick={() => onReply(message)}>
                  {t('actions.reply')}
                </ButtonLink>
              ) : null}
            </Box>
            <VisibleToComponent
              personRoleId={personRoleId}
              isMultiThread={isMultiThread}
            />
          </Box>
        ) : null}
      </Box>
    </StyledContainer>
  );
};

ThreadItemComponent.defaultProps = {
  onReply: null,
  buildings: null,
  isMultiThread: false,
};

ThreadItemComponent.propTypes = {
  message: PropTypes.instanceOf(Object).isRequired,
  isUserMessage: PropTypes.bool.isRequired,
  onReply: PropTypes.func,
  buildings: PropTypes.instanceOf(Object),
  isMultiThread: PropTypes.bool,
};

export default ThreadItemComponent;
